export const bynderImageUrl = (field: string) => `
  coalesce(
    ${field}.selectedUrl,
    ${field}.url,
    ${field}.datUrl,
    ${field}.previewUrl,
    ${field}.previewImg
  )
`;

export const compactBynderImageProjection = (field: string) => `
  select(
    defined(${field}) => {
      "_type": "bynder.asset",
      "id": ${field}.id,
      "name": ${field}.name,
      "description": ${field}.description,
      "alt": coalesce(${field}.textMetaproperties[name == 'Alt_text'][0].value, ${field}.alt),
      "isSvg": ${field}.extensions[0] == "svg",
      "selectedUrl": ${field}.selectedUrl,
      "url": coalesce(${field}.url, ${field}.selectedUrl, ${field}.datUrl, ${field}.previewUrl, ${field}.previewImg),
      "width": ${field}.width,
      "height": ${field}.height,
      "aspectRatio": ${field}.aspectRatio
    },
    null
  )
`;

export const computedInternalUrl = `
  select(
    defined(parentPage->parentPage->slug.current) => "/products/" + parentPage->parentPage->slug.current + "/" + parentPage->slug.current + "/" + slug.current,
    defined(parentPage->slug.current) => "/products/" + parentPage->slug.current + "/" + slug.current,
    _type == "productsList" => "/products/" + category->slug.current + "/" + slug.current,
    _type == "product" && defined(subcategory->parentCategory->slug.current) => "/products/" + subcategory->parentCategory->slug.current + "/" + subcategory->slug.current + "/" + slug.current,
    _type == "structaProductLevel1" => "/products/" + slug.current,
    _type == "articlesList" => "/" + select(
      type == "blogs" => "blog",
      type
    ),
    _type == "article" && defined(type) && type == "blogs" => "/blog/" + slug.current,
    _type == "article" && defined(type) => "/" + type + "/" + slug.current,
    _type == "documentPage" => "/documents/" + slug.current,
    _type == "redRec.quoteSettings" => "/quote",
    _type == "faqs" => "/faq",
    _type == "contactUs" => "/contact-us",
    _type == "storeLocator" => "/store-locator",
    _type == "homePage" => "/",
    "/" + slug.current
  )
`;

export const internalReferenceUrl = `
  select(
    type == "internal" =>
      internalReference->{
        "computedUrl": ${computedInternalUrl}
      }.computedUrl,
    url
  )
`;

export const directInternalReferenceUrl = `
  select(
    link.type == "internal" =>
      link.internalReference->{
        "computedUrl": ${computedInternalUrl}
      }.computedUrl,
    link.type == "external" => link.url
  )
`;

export const LOGO_PROJECTTION = `
  logo{
    ...,
    link{
      ...,
      "url": ${internalReferenceUrl},
    },
    "bynderLogo": ${compactBynderImageProjection('bynderLogo')},
    "imageUrl": coalesce(${bynderImageUrl('bynderLogo')}, logo.asset->url),
  }
`;

export const LOGO_PROJECTION = LOGO_PROJECTTION;

export const imageProjection = `
  ...,
  "alt": coalesce(alt, asset->altText),
  "title": coalesce(title, asset->title),
  "url": asset->url,
`;

type CompatibleImageProjectionOptions = {
  alias?: string;
  imageField?: string;
  bynderField?: string;
  alt?: string;
  title?: string;
};

export const compatibleImageProjection = ({
  alias = 'image',
  imageField = alias,
  bynderField = 'bynderImage',
  alt,
  title,
}: CompatibleImageProjectionOptions = {}) => {
  const resolvedAlt =
    alt ??
    `coalesce(${bynderField}.textMetaproperties[name == 'Alt_text'][0].value, ${bynderField}.alt, ${imageField}.alt, ${imageField}.asset->altText)`;
  const resolvedTitle =
    title ??
    `coalesce(${imageField}.title, ${imageField}.asset->title, ${imageField}.asset.title, ${bynderField}.name)`;

  return `
    "${alias}": select(
      defined(${imageField}) || defined(${bynderField}) => {
        "_type": coalesce(${imageField}._type, "image"),
        "asset": {
          "_id": coalesce(${bynderField}.id, ${imageField}.asset->_id, ${imageField}.asset._id),
          "_ref": ${imageField}.asset._ref,
          "_type": coalesce(${imageField}.asset->_type, ${imageField}.asset._type, "reference"),
          "url": coalesce(
            ${bynderField}.selectedUrl,
            ${bynderField}.url,
            ${bynderField}.datUrl,
            ${bynderField}.previewUrl,
            ${bynderField}.previewImg,
            ${imageField}.asset->url,
            ${imageField}.asset.url
          ),
          "metadata": {
            "lqip": ${imageField}.asset->metadata.lqip,
            "dimensions": {
              "width": coalesce(${bynderField}.width, ${imageField}.asset->metadata.dimensions.width),
              "height": coalesce(${bynderField}.height, ${imageField}.asset->metadata.dimensions.height),
              "aspectRatio": coalesce(${bynderField}.aspectRatio, ${imageField}.asset->metadata.dimensions.aspectRatio)
            }
          }
        },
        "crop": ${imageField}.crop,
        "hotspot": ${imageField}.hotspot,
        "alt": ${resolvedAlt},
        "title": ${resolvedTitle},
        "url": coalesce(
          ${bynderField}.selectedUrl,
          ${bynderField}.url,
          ${bynderField}.datUrl,
          ${bynderField}.previewUrl,
          ${bynderField}.previewImg,
          ${imageField}.url,
          ${imageField}.asset->url,
          ${imageField}.asset.url
        ),
        "width": coalesce(${bynderField}.width, ${imageField}.asset->metadata.dimensions.width),
        "height": coalesce(${bynderField}.height, ${imageField}.asset->metadata.dimensions.height),
        "bynderImage": ${compactBynderImageProjection(bynderField)}
      },
      null
    )
  `;
};
